import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X,
  Send,
  Bot,
  User,
  Crown,
  RefreshCw,
  Trash2,
  Paperclip,
  CheckCheck,
  Sparkles,
  AlertCircle,
  Loader2,
  Image as ImageIcon,
  Check,
  ChevronRight,
  ShieldCheck,
  Sliders,
} from 'lucide-react';
import { MessageVisualContent } from './MessageVisualContent';

export interface ChatMessage {
  id: string;
  sender: 'user' | 'luna';
  role: 'user' | 'luna' | 'assistant' | 'model';
  text: string;
  timestamp: string;
  mediaUrl?: string | null;
  toolsUsed?: string[];
  nodeUsed?: string;
  isOwner?: boolean;
}

interface KangavenaWhatsAppSimulatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  token?: string;
  tenant?: any;
  agentName?: string;
}

export const KangavenaWhatsAppSimulatorModal: React.FC<KangavenaWhatsAppSimulatorModalProps> = ({
  isOpen,
  onClose,
  token = '',
  tenant,
  agentName,
}) => {
  const [mode, setMode] = useState<'owner' | 'customer'>('customer');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState<string>('');
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);
  const [isResetting, setIsResetting] = useState<boolean>(false);
  const [attachedImage, setAttachedImage] = useState<{ base64: string; mime: string; name: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const effectiveToken =
    token ||
    (typeof window !== 'undefined'
      ? localStorage.getItem('tenet_client_token') || sessionStorage.getItem('tenet_client_token') || ''
      : '');

  const activeAgentName = agentName || tenant?.customAvatarName || tenant?.name || 'Luna AI';

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isSending]);

  // Fetch history when modal opens or mode changes
  const fetchHistory = useCallback(async () => {
    if (!effectiveToken) return;
    try {
      setIsLoadingHistory(true);
      const res = await fetch(`/api/tenet/whatsapp/test-chat/history?mode=${mode}`, {
        headers: { Authorization: `Bearer ${effectiveToken}` },
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.messages)) {
        const formatted: ChatMessage[] = data.messages.map((m: any, idx: number) => ({
          id: m.id || `msg_${idx}_${Date.now()}`,
          sender: m.sender === 'luna' || m.role === 'luna' || m.role === 'model' ? 'luna' : 'user',
          role: m.role || (m.sender === 'luna' ? 'luna' : 'user'),
          text: m.text || m.message || '',
          timestamp: m.time || m.timestamp || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          mediaUrl: m.mediaUrl || (Array.isArray(m.mediaUrls) ? m.mediaUrls[0] : null),
          toolsUsed: m.toolsUsed || [],
          nodeUsed: m.nodeUsed || 'Luna Core Hub',
          isOwner: m.isOwner,
        }));
        setMessages(formatted);
      } else {
        setMessages([]);
      }
    } catch (err) {
      console.error('[SimulatorModal] Fetch history error:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [effectiveToken, mode]);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
    }
  }, [isOpen, mode, fetchHistory]);

  if (!isOpen) return null;

  // Handle Reset / Clear
  const handleResetChat = async () => {
    if (!effectiveToken || isResetting) return;
    const confirmReset = window.confirm(
      `Are you sure you want to reset the test chat history for ${mode === 'owner' ? 'Store Owner' : 'Customer'} Mode?`
    );
    if (!confirmReset) return;

    try {
      setIsResetting(true);
      const res = await fetch('/api/tenet/whatsapp/test-chat/reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${effectiveToken}`,
        },
        body: JSON.stringify({ mode }),
      });
      const data = await res.json();
      if (data.success) {
        setMessages([]);
      }
    } catch (err) {
      console.error('[SimulatorModal] Reset error:', err);
    } finally {
      setIsResetting(false);
    }
  };

  // Handle Send Message
  const handleSendMessage = async (textOverride?: string) => {
    const textToSend = (textOverride !== undefined ? textOverride : inputText).trim();
    if ((!textToSend && !attachedImage) || isSending || !effectiveToken) return;

    const userMsgId = `usr_${Date.now()}`;
    const newMsg: ChatMessage = {
      id: userMsgId,
      sender: 'user',
      role: 'user',
      text: textToSend,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      mediaUrl: attachedImage?.base64,
      isOwner: mode === 'owner',
    };

    setMessages((prev) => [...prev, newMsg]);
    setInputText('');
    const currentAttachment = attachedImage;
    setAttachedImage(null);
    setIsSending(true);

    try {
      const payload: any = {
        message: textToSend,
        mode,
      };
      if (currentAttachment) {
        payload.mediaItems = [
          {
            base64: currentAttachment.base64,
            mime: currentAttachment.mime,
            name: currentAttachment.name,
          },
        ];
      }

      const res = await fetch('/api/tenet/whatsapp/test-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${effectiveToken}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (data.success) {
        const lunaMsg: ChatMessage = {
          id: `luna_${Date.now()}`,
          sender: 'luna',
          role: 'luna',
          text: data.reply || 'Message processed.',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          mediaUrl: data.mediaUrl || (Array.isArray(data.mediaUrls) ? data.mediaUrls[0] : null),
          toolsUsed: Array.isArray(data.toolsExecuted) ? data.toolsExecuted : (data.toolCalls?.map((t: any) => t.name) || []),
          nodeUsed: data.turn?.nodeUsed || (mode === 'owner' ? 'Store Owner Engine' : 'Luna Core Hub'),
          isOwner: mode === 'owner',
        };
        setMessages((prev) => [...prev, lunaMsg]);
      } else {
        const errorMsg: ChatMessage = {
          id: `err_${Date.now()}`,
          sender: 'luna',
          role: 'luna',
          text: `⚠️ Simulation Notice: ${data.error || 'Failed to process test message'}`,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        };
        setMessages((prev) => [...prev, errorMsg]);
      }
    } catch (err: any) {
      const errorMsg: ChatMessage = {
        id: `err_${Date.now()}`,
        sender: 'luna',
        role: 'luna',
        text: `⚠️ Network Error: ${err?.message || 'Connection failed'}`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsSending(false);
    }
  };

  // Handle File Upload
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setAttachedImage({
        base64,
        mime: file.type || 'image/jpeg',
        name: file.name,
      });
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Quick Prompt Pills
  const ownerPills = [
    'Add Micro Panda 12,000/day',
    'Change Mini Car rate to 10,000',
    'List all active categories',
    'Who are you and what can you do?',
  ];

  const customerPills = [
    'Hi, do you have cars for rent?',
    'How much to rent an Alto for 3 days?',
    'I want to book a car for tomorrow',
    'Where is your location?',
  ];

  const activePills = mode === 'owner' ? ownerPills : customerPills;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/85 backdrop-blur-md animate-in fade-in duration-200">
      {/* Device / Sandbox Container */}
      <div className="relative w-full max-w-lg h-[780px] max-h-[94vh] flex flex-col rounded-3xl overflow-hidden shadow-2xl border border-slate-700/70 bg-[#0b141a] text-[#e9edef] font-sans">
        
        {/* Top Header Bar */}
        <div className="flex flex-col bg-[#202c33] border-b border-slate-700/60 shrink-0">
          
          {/* Main Title & Controls */}
          <div className="flex items-center justify-between px-4 py-2.5">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="relative w-9 h-9 rounded-full bg-gradient-to-tr from-emerald-600 to-cyan-500 flex items-center justify-center shadow-md shrink-0">
                <Bot className="w-5 h-5 text-white" />
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-[#202c33]" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <h3 className="text-sm font-semibold text-white truncate">{activeAgentName}</h3>
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                    Active Flow
                  </span>
                </div>
                <p className="text-[11px] text-slate-400 flex items-center gap-1 truncate">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse inline-block" />
                  Real-Time WhatsApp Sandbox
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={handleResetChat}
                disabled={isResetting || isSending}
                title="Reset test conversation history"
                className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-slate-800/80 transition-colors disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/80 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Mode Switcher Segmented Control */}
          <div className="flex items-center justify-between px-3 py-1.5 bg-[#111b21] border-t border-slate-800/80 text-xs">
            <span className="text-[11px] font-medium text-slate-400 flex items-center gap-1">
              <Sliders className="w-3 h-3 text-slate-400" />
              Simulated Role:
            </span>
            <div className="flex items-center bg-[#202c33] p-0.5 rounded-lg border border-slate-700/50">
              <button
                type="button"
                onClick={() => setMode('owner')}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                  mode === 'owner'
                    ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-bold shadow-md'
                    : 'text-slate-300 hover:text-white'
                }`}
              >
                <Crown className={`w-3.5 h-3.5 ${mode === 'owner' ? 'text-slate-950' : 'text-amber-400'}`} />
                <span>Store Owner</span>
              </button>
              <button
                type="button"
                onClick={() => setMode('customer')}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                  mode === 'customer'
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-slate-950 font-bold shadow-md'
                    : 'text-slate-300 hover:text-white'
                }`}
              >
                <User className={`w-3.5 h-3.5 ${mode === 'customer' ? 'text-slate-950' : 'text-emerald-400'}`} />
                <span>Customer</span>
              </button>
            </div>
          </div>

          {/* Mode Guidance Sub-Banner */}
          <div className={`px-3 py-1 text-[11px] border-t transition-colors ${
            mode === 'owner' 
              ? 'bg-amber-950/30 border-amber-500/20 text-amber-200' 
              : 'bg-emerald-950/30 border-emerald-500/20 text-emerald-200'
          }`}>
            {mode === 'owner' ? (
              <span>👑 <strong>Owner Mode:</strong> Commands catalog, prices, policies & escalations.</span>
            ) : (
              <span>👤 <strong>Customer Mode:</strong> Tests bookings, pricing queries & lead capture.</span>
            )}
          </div>
        </div>

        {/* Quick Suggestion Pills */}
        <div className="px-3 py-2 bg-[#0c1317] border-b border-slate-800/80 flex items-center gap-1.5 overflow-x-auto no-scrollbar shrink-0">
          <span className="text-[10px] uppercase font-semibold text-slate-500 shrink-0">Try:</span>
          {activePills.map((pill, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handleSendMessage(pill)}
              disabled={isSending}
              className="text-[11px] px-2.5 py-0.5 rounded-full bg-slate-800/80 hover:bg-slate-700/90 text-slate-300 border border-slate-700/60 whitespace-nowrap transition-colors disabled:opacity-50"
            >
              {pill}
            </button>
          ))}
        </div>

        {/* Chat Transcript Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[radial-gradient(#1e293b_1px,transparent_1px)] [background-size:16px_16px]">
          {isLoadingHistory ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-400 text-xs gap-2">
              <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
              <span>Loading conversation history...</span>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <div className="w-12 h-12 rounded-full bg-slate-800/80 border border-slate-700 flex items-center justify-center mb-3">
                <ShieldCheck className="w-6 h-6 text-emerald-400" />
              </div>
              <h4 className="text-sm font-semibold text-white mb-1">
                {mode === 'owner' ? 'Store Owner Test Sandbox' : 'Customer Test Sandbox'}
              </h4>
              <p className="text-xs text-slate-400 max-w-xs leading-relaxed">
                Test your active Luna flow in real-time. No actual WhatsApp account or QR code scan is required.
              </p>
              <div className="mt-4 p-2.5 rounded-xl bg-slate-900/60 border border-slate-800 text-[11px] text-slate-400 text-left max-w-sm">
                {mode === 'owner' ? (
                  <>
                    <p className="font-semibold text-amber-300 mb-1">Owner Mode capabilities:</p>
                    <ul className="list-disc list-inside space-y-0.5 text-slate-300">
                      <li>Add/update vehicle categories & pricing</li>
                      <li>Upload agreements, manuals, or documents</li>
                      <li>Test feasibility checks and developer escalation</li>
                    </ul>
                  </>
                ) : (
                  <>
                    <p className="font-semibold text-emerald-300 mb-1">Customer Mode capabilities:</p>
                    <ul className="list-disc list-inside space-y-0.5 text-slate-300">
                      <li>Test polite respectful customer assistance</li>
                      <li>Inquire about vehicle rates and availability</li>
                      <li>Simulate bookings and automated lead alerts</li>
                    </ul>
                  </>
                )}
              </div>
            </div>
          ) : (
            messages.map((msg) => {
              const isUser = msg.sender === 'user';
              return (
                <div
                  key={msg.id}
                  className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}
                >
                  <div
                    className={`relative px-3.5 py-2.5 rounded-2xl max-w-[85%] text-xs shadow-md ${
                      isUser
                        ? msg.isOwner
                          ? 'bg-amber-700/40 text-amber-100 border border-amber-500/30 rounded-tr-none'
                          : 'bg-[#005c4b] text-white rounded-tr-none'
                        : 'bg-[#202c33] text-[#e9edef] rounded-tl-none border border-slate-700/40'
                    }`}
                  >
                    {/* Media Attachment Preview */}
                    {msg.mediaUrl && (
                      <div className="mb-2 rounded-lg overflow-hidden border border-black/20 bg-black/40 max-h-48">
                        <img
                          src={msg.mediaUrl}
                          alt="Media"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}

                    {/* Message Text & Collapsible Vision/OCR */}
                    <MessageVisualContent
                      text={msg.text}
                      isIncoming={isUser}
                      messageKey={`sim_msg_${msg.id}`}
                      hasImages={Boolean(msg.mediaUrl)}
                      theme="whatsapp"
                    />

                    {/* Executed Tools Badge */}
                    {!isUser && msg.toolsUsed && msg.toolsUsed.length > 0 && (
                      <div className="mt-2 pt-1.5 border-t border-slate-600/30 flex flex-wrap items-center gap-1">
                        <span className="text-[10px] text-emerald-400 font-medium flex items-center gap-1">
                          <Sparkles className="w-2.5 h-2.5" />
                          Tool:
                        </span>
                        {msg.toolsUsed.map((tool, idx) => (
                          <span
                            key={idx}
                            className="px-1.5 py-0.5 rounded bg-emerald-950/60 border border-emerald-500/30 text-[10px] text-emerald-300 font-mono"
                          >
                            {tool}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Timestamp & Meta */}
                    <div className="flex items-center justify-end gap-1 mt-1 text-[10px] text-slate-400">
                      <span>{msg.timestamp}</span>
                      {isUser && <CheckCheck className="w-3.5 h-3.5 text-[#53bdeb]" />}
                    </div>
                  </div>
                </div>
              );
            })
          )}

          {/* Typing Indicator */}
          {isSending && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-2xl rounded-tl-none bg-[#202c33] border border-slate-700/40 w-fit text-xs text-slate-300 shadow">
              <Bot className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
              <span>{activeAgentName} is typing</span>
              <span className="flex gap-0.5">
                <span className="w-1 h-1 rounded-full bg-emerald-400 animate-bounce" />
                <span className="w-1 h-1 rounded-full bg-emerald-400 animate-bounce [animation-delay:0.2s]" />
                <span className="w-1 h-1 rounded-full bg-emerald-400 animate-bounce [animation-delay:0.4s]" />
              </span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Attached Media Staging Preview */}
        {attachedImage && (
          <div className="px-4 py-2 bg-[#111b21] border-t border-slate-800 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-lg overflow-hidden border border-slate-700 shrink-0">
                <img src={attachedImage.base64} alt="Attached" className="w-full h-full object-cover" />
              </div>
              <span className="text-xs text-slate-300 truncate">{attachedImage.name}</span>
            </div>
            <button
              type="button"
              onClick={() => setAttachedImage(null)}
              className="p-1 text-slate-400 hover:text-rose-400 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Bottom Input Area */}
        <div className="p-3 bg-[#202c33] border-t border-slate-700/60 shrink-0">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage();
            }}
            className="flex items-center gap-2"
          >
            {/* File Upload Trigger */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*,.pdf"
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              title="Attach photo or document"
              className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-700/60 transition-colors"
            >
              <Paperclip className="w-4 h-4" />
            </button>

            {/* Input Field */}
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder={
                mode === 'owner'
                  ? 'Message as Store Owner (e.g. Add car, change rate)...'
                  : 'Message as Customer (e.g. Inquire about cars)...'
              }
              disabled={isSending}
              className="flex-1 bg-[#2a3942] border border-slate-600/40 rounded-xl px-3.5 py-2 text-xs text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500/70 transition-colors disabled:opacity-50"
            />

            {/* Send Button */}
            <button
              type="submit"
              disabled={(!inputText.trim() && !attachedImage) || isSending}
              className={`p-2 rounded-xl transition-all shadow-md ${
                mode === 'owner'
                  ? 'bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold disabled:opacity-40'
                  : 'bg-[#00a884] hover:bg-[#00c59b] text-white disabled:opacity-40'
              }`}
            >
              {isSending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </form>
        </div>

      </div>
    </div>
  );
};
